import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js";

import { CloudinaryFile } from "../../types/cloudinary_custom_types"

/**
 * Stimulus controller for handling media uploads via Turbo Streams.
 * 
 * This controller processes file uploads by sending them to a Rails endpoint
 * that responds with Turbo Stream HTML to update the page dynamically.
 * 
 * @example
 * ```html
 * <div data-controller="media-input--turbo"
 *      data-media-input--turbo-endpoint-value="/content_media">
 *   <!-- File upload interface -->
 * </div>
 * ```
 * 
 * @remarks
 * - Endpoint must respond with "text/vnd.turbo-stream.html" content type
 * - File data is sent as JSON in the request body
 * - La réponse est rendue par @rails/request.js lui-même (FetchRequest#perform) : la rendre
 *   ici aussi la jouait deux fois — invisible dans la bibliothèque, dont le prepend se
 *   dédoublonne par id, mais deux vignettes (et deux poses) dans la toolbox
 * 
 * @see {@link https://turbo.hotwired.dev/handbook/streams} Turbo Streams documentation
 */
export default class extends Controller<HTMLElement> {
  static values = {
    endpoint: String,
  } as const;

  /** API endpoint URL for file uploads */
  declare readonly endpointValue: string;

  /**
   * Handles file uploads by sending them to the configured endpoint.
   * The endpoint should respond with Turbo Stream HTML to update the UI.
   * 
   * @param file - The uploaded file from Cloudinary
   * @throws Will throw if the network request fails
   * 
   * @example
   * ```typescript
   * // Called automatically when a file is uploaded
   * await this.onFileAdded({
   *   identifier: "abc123",
   *   signature: "def456", 
   *   url: "https://cloudinary.com/image.jpg"
   * });
   * ```
   */
  async onFileAdded(file: CloudinaryFile, { batch }: { batch?: string } = {}): Promise<void> {
    // Le lot du dépôt revient dans la réponse : la toolbox y retrouve la cible figée.
    const body = JSON.stringify({
      content_medium: {
        medium: file.identifier + "#" + file.signature,
        kind: 'upload'
      },
      upload_batch: batch,
    });

    await post(this.endpointValue, {
      body,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json",
      },
    });
  }
}
